import { PersonFieldSection } from './PersonFields';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  getProfileLayout,
  resetProfileLayout,
  updateProfileLayout,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Eye,
  EyeOff,
  GripVertical,
  RotateCcw,
  Save,
  Settings2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const MIN_SECTION_WIDTH = 3;
const MAX_SECTION_WIDTH = 12;
// One grid column is roughly this many pixels on a typical desktop; the drag
// converts pixels to column steps so resizing feels direct rather than jumpy.
const PIXELS_PER_COLUMN = 90;

// A height of 0 means "as tall as its content". Anything else fixes the height
// and the card scrolls inside, so making a section shorter hides nothing.
const AUTO_SECTION_HEIGHT = 0;
const MIN_SECTION_HEIGHT = 120;
const MAX_SECTION_HEIGHT = 1200;

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

/* Loads, edits and saves the section layout for a profile page.

   A page is assembled from one or more surfaces: the student page renders the
   shared person sections and then the student ones, and the admissions page
   renders those two plus its own tail. Passing them all to one controller is
   what lets a single Customize button edit the whole page while each surface
   is still stored, saved and reset on its own — so the arrangement of the
   shared spine is genuinely one arrangement, not two that have to be kept
   matching by hand.

   The layout is shared by every admin, so entering edit mode snapshots it and
   Cancel restores the snapshot rather than refetching. */
export function useProfileLayout(surface) {
  // One surface or several; a plain string keeps the original call style.
  const surfaces = useMemo(
    () => (Array.isArray(surface) ? surface : [surface]).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Array.isArray(surface) ? surface.join('|') : surface],
  );

  const [layouts, setLayouts] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!surfaces.length) return undefined;
    let cancelled = false;
    Promise.all(
      surfaces.map((name) => getProfileLayout(name)
        .then(({ data }) => [name, data])
        // A missing layout must not blank the page: its sections fall back to
        // their natural order, and the other surfaces still load.
        .catch((error) => {
          console.error(error);
          return [name, null];
        })),
    ).then((entries) => {
      if (!cancelled) setLayouts(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [surfaces]);

  // Surface order is page order: the shared spine first, then whatever the
  // page adds after it.
  const sections = useMemo(() => {
    if (!layouts) return [];
    return surfaces.flatMap((name) => safeList(layouts[name]?.sections));
  }, [layouts, surfaces]);

  const surfaceBySectionId = useMemo(() => {
    const index = {};
    if (!layouts) return index;
    surfaces.forEach((name) => {
      safeList(layouts[name]?.sections).forEach((section) => {
        index[section.id] = name;
      });
    });
    return index;
  }, [layouts, surfaces]);

  const sectionById = useMemo(
    () => Object.fromEntries(sections.map((section, index) => [section.id, { ...section, order: index }])),
    [sections],
  );

  const patchSection = useCallback((id, patch) => {
    setLayouts((current) => {
      if (!current) return current;
      const name = surfaceBySectionId[id];
      if (!name) return current;
      return {
        ...current,
        [name]: {
          ...current[name],
          sections: safeList(current[name]?.sections).map(
            (section) => (section.id === id ? { ...section, ...patch } : section),
          ),
        },
      };
    });
  }, [surfaceBySectionId]);

  const moveSection = useCallback((sourceId, targetId) => {
    setLayouts((current) => {
      if (!current) return current;
      const name = surfaceBySectionId[sourceId];
      // Surfaces are page regions in their own right, so a card cannot be
      // dragged out of the one it belongs to — the same rule groups enforce
      // within a surface.
      if (!name || surfaceBySectionId[targetId] !== name) {
        toast.error('Sections can only be reordered within their own group.');
        return current;
      }
      const list = safeList(current[name]?.sections);
      const from = list.findIndex((section) => section.id === sourceId);
      const to = list.findIndex((section) => section.id === targetId);
      if (from < 0 || to < 0 || from === to) return current;
      if (list[from].group !== list[to].group) {
        toast.error('Sections can only be reordered within their own group.');
        return current;
      }
      const next = [...list];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...current, [name]: { ...current[name], sections: next } };
    });
  }, [surfaceBySectionId]);

  const begin = useCallback(() => {
    setSnapshot(layouts);
    setEditMode(true);
  }, [layouts]);

  const cancel = useCallback(() => {
    if (snapshot) setLayouts(snapshot);
    setSnapshot(null);
    setEditMode(false);
  }, [snapshot]);

  const save = useCallback(async () => {
    if (!layouts) return;
    try {
      setSaving(true);
      const saved = await Promise.all(
        surfaces.map((name) => updateProfileLayout(name, { sections: safeList(layouts[name]?.sections) })
          .then(({ data }) => [name, data])),
      );
      setLayouts((current) => ({ ...current, ...Object.fromEntries(saved) }));
      setSnapshot(null);
      setEditMode(false);
      toast.success('Layout saved for everyone');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not save the layout');
    } finally {
      setSaving(false);
    }
  }, [layouts, surfaces]);

  const reset = useCallback(async () => {
    try {
      setSaving(true);
      const restored = await Promise.all(
        surfaces.map((name) => resetProfileLayout(name).then(({ data }) => [name, data])),
      );
      setLayouts(Object.fromEntries(restored));
      setSnapshot(null);
      setEditMode(false);
      toast.success('Layout restored to its default');
    } catch (error) {
      console.error(error);
      toast.error('Could not reset the layout');
    } finally {
      setSaving(false);
    }
  }, [surfaces]);

  return {
    layout: layouts,
    sections,
    sectionById,
    editMode,
    saving,
    // There is something to customize only once a surface has actually
    // loaded. A page whose layouts all failed to fetch still renders its
    // sections in their natural order, but offers no Customize button.
    hasLayout: Boolean(layouts) && surfaces.some((name) => layouts[name]),
    begin,
    cancel,
    save,
    reset,
    patchSection,
    moveSection,
  };
}

/* The Customize / Save / Cancel / Reset controls for a profile page. */
export function ProfileLayoutToolbar({ controller, label = 'Customize layout', className }) {
  const { editMode, saving, begin, cancel, save, reset, hasLayout } = controller;
  if (!hasLayout) return null;

  if (!editMode) {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={begin}
        className={cn('border-[#D4AF37]/45 text-[#8A6508]', className)}
      >
        <Settings2 className="mr-2 size-4" />
        {label}
      </Button>
    );
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Button type="button" variant="outline" onClick={reset} disabled={saving} className="border-[#062B1A]/20">
        <RotateCcw className="mr-2 size-4" />
        Reset default
      </Button>
      <Button type="button" variant="ghost" onClick={cancel} disabled={saving}>
        Cancel
      </Button>
      <Button
        type="button"
        onClick={save}
        disabled={saving}
        className="bg-[#062B1A] text-white hover:bg-[#041B10]"
      >
        <Save className="mr-2 size-4" />
        {saving ? 'Saving...' : 'Save layout'}
      </Button>
    </div>
  );
}

/* Drag handles for a section. `axis` picks which edge, and the corner drags
   both at once. The element's own measured height seeds a vertical drag, so
   grabbing a section that is still on auto starts from where it actually is
   rather than jumping to some default. */
function ResizeGrip({ section, axis, hostRef, onResize }) {
  const begin = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = Number(section.width) || MAX_SECTION_WIDTH;
    const startHeight = Number(section.height) || hostRef.current?.offsetHeight || MIN_SECTION_HEIGHT;

    const drag = (moveEvent) => {
      const patch = {};
      if (axis !== 'y') {
        const steps = Math.round((moveEvent.clientX - startX) / PIXELS_PER_COLUMN);
        patch.width = Math.max(MIN_SECTION_WIDTH, Math.min(MAX_SECTION_WIDTH, startWidth + steps));
      }
      if (axis !== 'x') {
        const next = startHeight + (moveEvent.clientY - startY);
        patch.height = Math.max(MIN_SECTION_HEIGHT, Math.min(MAX_SECTION_HEIGHT, Math.round(next)));
      }
      onResize(patch);
    };
    const finish = () => {
      window.removeEventListener('pointermove', drag);
      window.removeEventListener('pointerup', finish);
    };
    window.addEventListener('pointermove', drag);
    window.addEventListener('pointerup', finish);
  };

  const placement = {
    x: 'top-1/2 -right-1 h-16 w-2 -translate-y-1/2 cursor-col-resize',
    y: 'left-1/2 -bottom-1 h-2 w-16 -translate-x-1/2 cursor-row-resize',
    both: '-bottom-1 -right-1 h-3 w-3 cursor-nwse-resize',
  }[axis];

  const label = {
    x: `Resize ${section.title} width`,
    y: `Resize ${section.title} height`,
    both: `Resize ${section.title}`,
  }[axis];

  return (
    <span
      role="separator"
      aria-orientation={axis === 'y' ? 'horizontal' : 'vertical'}
      aria-label={label}
      onPointerDown={begin}
      className={cn(
        /* Width and height only take effect from `lg` up, so below that a
           grip would be a handle that visibly does nothing. Reordering and
           hiding still work at every size. */
        'absolute z-30 hidden rounded-full bg-[#D4AF37] ring-2 ring-white lg:block',
        placement,
      )}
    />
  );
}

/* Wraps one card so the layout can position, size, hide and rename it.

   Sections keep their place in the JSX and are moved with CSS `order`, which
   means a page only has to wrap its cards rather than restructure them. This
   requires the wrapped cards to be grid siblings — see ProfileSectionGrid. */
export function ProfileSection({ id, controller, children, className }) {
  const { sectionById, editMode, patchSection, moveSection } = controller;
  const hostRef = useRef(null);
  const section = sectionById[id];

  const content = <PersonFieldSection id={id}>{children}</PersonFieldSection>;
  // Field configuration also works while the section layout is loading.
  if (!section) return content;
  if (!section.visible && !editMode) return null;

  const fixedHeight = Number(section.height) > 0 ? Number(section.height) : null;
  /* Width and height are handed to CSS as custom properties rather than set
     directly, because both only apply from `lg` up -- see `.profile-section`
     in index.css. The twelve-column grid does not exist below that, and a
     span set against it made a half-width card try to span six tracks of a
     one-column grid, which the browser satisfied by inventing five more. */
  const style = {
    order: section.order,
    '--profile-section-span': section.width,
    ...(fixedHeight ? { '--profile-section-height': `${fixedHeight}px` } : {}),
  };

  // With a fixed height the card scrolls inside its own box, so shortening a
  // section never cuts content off -- it just puts it behind a scrollbar.
  // Narrow screens ignore the height, so the scroll box is only wanted there.
  const body = fixedHeight
    ? <div className="min-h-0 lg:flex-1 lg:overflow-y-auto">{content}</div>
    : content;

  if (!editMode) {
    return (
      <div
        ref={hostRef}
        style={style}
        className={cn('profile-section flex min-w-0 flex-col', className)}
      >
        {body}
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      style={style}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        moveSection(event.dataTransfer.getData('text/plain'), id);
      }}
      className={cn(
        'profile-section relative flex min-w-0 flex-col rounded-xl ring-2 ring-[#D4AF37]/45',
        !section.visible && 'opacity-45',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2 rounded-t-xl bg-[#062B1A] px-2 py-1.5">
        {/* Only the grip starts a drag, so the rename box stays clickable. */}
        <span
          draggable
          onDragStart={(event) => event.dataTransfer.setData('text/plain', id)}
          aria-label={`Move ${section.title}`}
          className="shrink-0 cursor-grab rounded p-0.5 text-[#D4AF37] hover:bg-white/10"
        >
          <GripVertical aria-hidden="true" className="size-4" />
        </span>
        <Input
          value={section.title}
          onChange={(event) => patchSection(id, { title: event.target.value })}
          aria-label={`Rename ${section.title}`}
          maxLength={80}
          className="h-7 min-w-0 flex-1 border-white/25 bg-white/10 px-2 text-xs font-semibold text-white focus-visible:ring-[#F6D76B]"
        />
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-white/55">
          {section.width}/12
        </span>
        <button
          type="button"
          onClick={() => patchSection(id, {
            height: fixedHeight ? AUTO_SECTION_HEIGHT : (hostRef.current?.offsetHeight || MIN_SECTION_HEIGHT),
          })}
          title={fixedHeight ? 'Back to fitting its content' : 'Fix the current height'}
          aria-label={fixedHeight ? `Set ${section.title} height to auto` : `Fix ${section.title} height`}
          className="hidden shrink-0 rounded px-1 text-[10px] font-semibold uppercase tracking-wide text-white/55 hover:bg-white/15 hover:text-white lg:block"
        >
          {fixedHeight ? `${fixedHeight}px` : 'auto'}
        </button>
        <button
          type="button"
          onClick={() => patchSection(id, { visible: !section.visible })}
          aria-label={section.visible ? `Hide ${section.title}` : `Show ${section.title}`}
          className="shrink-0 rounded p-1 text-white/75 hover:bg-white/15 hover:text-white"
        >
          {section.visible ? <Eye aria-hidden="true" className="size-4" /> : <EyeOff aria-hidden="true" className="size-4" />}
        </button>
      </div>
      {/* pointer-events-none keeps the underlying form inert while arranging, so
          a drag cannot accidentally toggle a control inside the card. */}
      <div className={cn('pointer-events-none select-none', fixedHeight && 'min-h-0 lg:flex-1 lg:overflow-y-auto')}>
        {content}
      </div>
      <ResizeGrip section={section} axis="x" hostRef={hostRef} onResize={(patch) => patchSection(id, patch)} />
      <ResizeGrip section={section} axis="y" hostRef={hostRef} onResize={(patch) => patchSection(id, patch)} />
      <ResizeGrip section={section} axis="both" hostRef={hostRef} onResize={(patch) => patchSection(id, patch)} />
    </div>
  );
}

/* The 12-column grid that ProfileSection children position themselves in. */
export function ProfileSectionGrid({ children, className }) {
  return (
    <div className={cn('grid grid-cols-1 gap-6 lg:grid-cols-12', className)}>
      {children}
    </div>
  );
}

/* For a wrapper that sits between the grid and its sections. `display: contents`
   lets the cards inside it act as grid items of the outer grid, so an existing
   sub-grid does not have to be unpicked from the JSX. */
export function ProfileSectionPassthrough({ children, className }) {
  return <div style={{ display: 'contents' }} className={className}>{children}</div>;
}
